import dayjs from 'dayjs';
import {getCheckinDeadline} from '../shared/checkin-deadline.utils';
import {
  isActiveWorkspaceUser,
  isPendingWorkspaceInvitation,
} from '@utils/active-workspace-user';
import {
  ICheckinCycle,
  ICheckinCycleDetail,
} from '@pages/dashboard/company-legacy/company-components/company-checkins/company-checkins.interface';

export type ManagedUser = {
  id?: string;
  status?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  jobTitle?: string;
  timezone?: string;
  customTimezone?: string;
  avatar?: {url?: string};
};

export type ManagedAudienceUser = ManagedUser & {
  status?: string;
  deletedAt?: string | null;
  emailVerified?: boolean;
  groups?: Array<
    | string
    | {
        id?: string;
        _id?: string;
        group?: string | {id?: string; _id?: string};
      }
  >;
};

export type ManagedAudienceGroup = {
  id?: string;
  _id?: string;
  members?: unknown[];
};

export type ManagedCycle = ICheckinCycle &
  Partial<ICheckinCycleDetail> & {
    isPaused?: boolean;
  };

export type ManagedSubmission = {
  id?: string;
  cycle?: string | ManagedCycle;
  cycleId?: string;
  cycleName?: string;
  user?: string | ManagedUser;
  createdAt?: string;
  updatedAt?: string;
  submittedAt?: string;
  isSubmitted?: boolean;
  isExcused?: boolean;
  isActive?: boolean;
  availabilityState?: 'scheduled';
  dueDate?: string;
  dueAt?: string;
  dueDay?: string;
  status?: string;
  year?: number;
  weekOfYear?: number;
  checkinCode?: string;
};

export type ParticipationStats = {
  submittedCount: number;
  pendingCount: number;
  totalCount: number;
  percentage: number;
};

export const getSubmissionRows = (
  response: {data?: unknown[]} | false | null | undefined,
): ManagedSubmission[] =>
  response && Array.isArray(response.data)
    ? (response.data as ManagedSubmission[])
    : [];

export const markSubmissionExcusedInList = <
  T extends {data?: ManagedSubmission[]} | false | null | undefined
>(
  response: T,
  submissionId: string,
): T => {
  if (!response || !Array.isArray(response.data)) return response;

  const submissionIndex = response.data.findIndex(
    (submission) => submission.id === submissionId,
  );
  if (submissionIndex === -1) return response;

  const data = [...response.data];
  data[submissionIndex] = {...data[submissionIndex], isExcused: true};
  return {...response, data};
};

export const getAdminSubmissionFilter = (cycleId?: string) => ({
  ...(cycleId ? {cycleId} : {}),
  paginate: false,
});

export const getSubmissionCycleId = (submission: ManagedSubmission) =>
  typeof submission.cycle === 'string'
    ? submission.cycle
    : submission.cycle?.id || submission.cycleId;

export const getManagedSubmissionUserId = (submission?: ManagedSubmission) =>
  typeof submission?.user === 'object' ? submission.user?.id : submission?.user;

export const withoutPendingInvitationSubmissions = (
  submissions: ManagedSubmission[],
  workspaceUsers: Array<{id?: string; _id?: string; status?: string}> = [],
) => {
  const pendingIds = new Set(
    workspaceUsers
      .filter(isPendingWorkspaceInvitation)
      .map((user) => user.id || user._id)
      .filter(Boolean),
  );
  return submissions.filter((submission) => {
    if (
      typeof submission.user === 'object' &&
      isPendingWorkspaceInvitation(submission.user)
    )
      return false;
    const userId = getManagedSubmissionUserId(submission);
    return !userId || !pendingIds.has(userId);
  });
};

export const getEffectiveCycleStatus = (cycle: ManagedCycle) =>
  cycle.status === 'active' && cycle.isPaused ? 'paused' : cycle.status;

export const isSubmissionSubmitted = (submission: ManagedSubmission) =>
  submission.isSubmitted === true || Boolean(submission.submittedAt);

export const submissionDaysOverdue = (
  submission: ManagedSubmission,
): number => {
  if (
    isSubmissionSubmitted(submission) ||
    submission.isExcused ||
    submission.availabilityState === 'scheduled'
  )
    return 0;
  return getCheckinDeadline(submission).daysOverdue;
};

export type CheckinSubmissionKind =
  | 'submitted'
  | 'excused'
  | 'scheduled'
  | 'overdue'
  | 'pending'
  | 'missed';

export type CheckinSubmissionStatus = {
  kind: CheckinSubmissionKind;
  daysOverdue: number;
};

export const getCheckinSubmissionStatus = (
  submission: ManagedSubmission,
  {isLatestOccurrence = true}: {isLatestOccurrence?: boolean} = {},
): CheckinSubmissionStatus => {
  if (submission.isExcused) return {kind: 'excused', daysOverdue: 0};
  if (isSubmissionSubmitted(submission))
    return {kind: 'submitted', daysOverdue: 0};
  if (submission.availabilityState === 'scheduled')
    return {kind: 'scheduled', daysOverdue: 0};
  if (!isLatestOccurrence) return {kind: 'missed', daysOverdue: 0};

  const deadline = getCheckinDeadline(submission);
  return {
    kind: deadline.isOverdue ? 'overdue' : 'pending',
    daysOverdue: deadline.daysOverdue,
  };
};

export const checkinSubmissionStatusLabel = ({
  kind,
  daysOverdue,
}: CheckinSubmissionStatus): string => {
  if (kind === 'overdue')
    return daysOverdue
      ? `${daysOverdue} day${daysOverdue === 1 ? '' : 's'} overdue`
      : 'Overdue';
  return {
    submitted: 'Submitted',
    excused: 'Excused',
    scheduled: 'Scheduled',
    pending: 'Not Submitted',
    missed: 'Missed',
  }[kind];
};

export const getSubmittedAt = (submission: ManagedSubmission) =>
  submission.submittedAt ||
  (isSubmissionSubmitted(submission) ? submission.updatedAt : undefined);

export const getParticipationStats = (
  submissions: ManagedSubmission[],
): ParticipationStats => {
  const eligibleSubmissions = submissions.filter(
    (submission) => !submission.isExcused,
  );
  const submittedCount = eligibleSubmissions.filter(isSubmissionSubmitted)
    .length;
  const totalCount = eligibleSubmissions.length;

  return {
    submittedCount,
    pendingCount: totalCount - submittedCount,
    totalCount,
    percentage: totalCount
      ? Math.round((submittedCount / totalCount) * 100)
      : 0,
  };
};

const getReferenceIds = (reference: unknown): string[] => {
  if (typeof reference === 'string') return [reference];
  if (!reference || typeof reference !== 'object') return [];

  const value = reference as {
    id?: string;
    _id?: string;
    group?: string | {id?: string; _id?: string};
  };
  const groupIds =
    typeof value.group === 'string'
      ? [value.group]
      : value.group
      ? [value.group.id, value.group._id]
      : [];

  return [value.id, value._id, ...groupIds].filter((id): id is string =>
    Boolean(id),
  );
};

const getGroupMemberIds = (
  group: ManagedAudienceGroup,
  knownUserIds: Set<string>,
) =>
  (group.members || []).flatMap((member) => {
    if (typeof member === 'string') return [member];
    if (!member || typeof member !== 'object') return [];
    const value = member as {
      id?: string;
      _id?: string;
      user?: string | {id?: string; _id?: string};
      member?: string | {id?: string; _id?: string};
    };
    const candidate = value.user || value.member || value;
    return getReferenceIds(candidate).filter((id) => knownUserIds.has(id));
  });

const getUsersInGroups = (
  groupIds: string[],
  users: ManagedAudienceUser[],
  groups: ManagedAudienceGroup[],
) => {
  const selectedGroups = new Set(groupIds);
  if (!selectedGroups.size) return new Set<string>();

  const userIds = new Set<string>();
  users.forEach((user) => {
    if (
      user.id &&
      (user.groups || []).some((group) =>
        getReferenceIds(group).some((id) => selectedGroups.has(id)),
      )
    ) {
      userIds.add(user.id);
    }
  });

  const knownUserIds = new Set(
    users.map((user) => user.id).filter((id): id is string => Boolean(id)),
  );
  groups.forEach((group) => {
    const groupId = group.id || group._id;
    if (!groupId || !selectedGroups.has(groupId)) return;
    getGroupMemberIds(group, knownUserIds).forEach((id) => userIds.add(id));
  });

  return userIds;
};

export const resolveCycleAudience = (
  cycle: ManagedCycle,
  workspaceUsers: ManagedAudienceUser[],
  workspaceGroups: ManagedAudienceGroup[] = [],
): ManagedAudienceUser[] => {
  const hasAudienceDefinition = [
    cycle.includedGroups,
    cycle.includedIndividuals,
    cycle.excludedGroups,
    cycle.excludedIndividuals,
  ].some(Array.isArray);
  if (!hasAudienceDefinition) return [];

  const users = workspaceUsers.filter(isActiveWorkspaceUser);
  const includedGroups = cycle.includedGroups || [];
  const includedIndividuals = cycle.includedIndividuals || [];
  const excludedGroups = cycle.excludedGroups || [];
  const excludedIndividuals = cycle.excludedIndividuals || [];
  const everyone = !includedGroups.length && !includedIndividuals.length;
  const includedUserIds = everyone
    ? new Set(
        users.map((user) => user.id).filter((id): id is string => Boolean(id)),
      )
    : getUsersInGroups(includedGroups, users, workspaceGroups);
  includedIndividuals.forEach((id) => includedUserIds.add(id));

  const excludedUserIds = getUsersInGroups(
    excludedGroups,
    users,
    workspaceGroups,
  );
  excludedIndividuals.forEach((id) => excludedUserIds.add(id));

  return users.filter(
    (user) =>
      Boolean(user.id && includedUserIds.has(user.id)) &&
      !excludedUserIds.has(user.id as string),
  );
};

export const mergeAudienceWithOccurrence = (
  submissions: ManagedSubmission[],
  expectedAudience: ManagedAudienceUser[],
  cycle: ManagedCycle,
): ManagedSubmission[] => {
  if (!expectedAudience.length) return submissions;

  const submissionsByUserId = new Map<string, ManagedSubmission>();
  submissions.forEach((submission) => {
    const userId = getManagedSubmissionUserId(submission);
    if (userId) submissionsByUserId.set(userId, submission);
  });
  const expectedUserIds = new Set(
    expectedAudience
      .map((user) => user.id)
      .filter((id): id is string => Boolean(id)),
  );
  const merged = expectedAudience.map((user) => {
    const existing = user.id ? submissionsByUserId.get(user.id) : undefined;
    return (
      existing || {
        cycle,
        user,
        isActive: false,
        isSubmitted: false,
        availabilityState: 'scheduled' as const,
      }
    );
  });

  submissions.forEach((submission) => {
    const userId = getManagedSubmissionUserId(submission);
    if (!userId || !expectedUserIds.has(userId)) merged.push(submission);
  });

  return merged;
};

const validTimezone = (timezone?: string) => {
  if (!timezone) return undefined;
  try {
    Intl.DateTimeFormat('en-GB', {timeZone: timezone}).format();
    return timezone;
  } catch {
    return undefined;
  }
};

export const getSubmissionTimezone = (
  submission: ManagedSubmission,
  cycle: ManagedCycle,
  workspaceTimezone?: string,
) => {
  if (cycle.config?.timezone === 'workspace') {
    return validTimezone(workspaceTimezone) || 'UTC';
  }

  const user =
    typeof submission.user === 'object' ? submission.user : undefined;
  return (
    validTimezone(user?.customTimezone) ||
    validTimezone(user?.timezone) ||
    validTimezone(workspaceTimezone) ||
    'UTC'
  );
};

export const getLocalDateKey = (date: string, timezone = 'UTC') => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: validTimezone(timezone) || 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(date));
  const value = (type: string) =>
    parts.find((part) => part.type === type)?.value;
  return `${value('year')}-${value('month')}-${value('day')}`;
};

export const getOccurrenceKey = (
  submission: ManagedSubmission,
  cycle: ManagedCycle,
  workspaceTimezone?: string,
) => {
  const date = submission.createdAt || submission.updatedAt;
  if (!date) return `${submission.year || ''}-W${submission.weekOfYear || ''}`;
  return getLocalDateKey(
    date,
    getSubmissionTimezone(submission, cycle, workspaceTimezone),
  );
};

export type CycleOccurrence = {
  key: string;
  label: string;
  submissions: ManagedSubmission[];
};

export const buildOccurrenceGroups = (
  submissions: ManagedSubmission[],
  cycle: ManagedCycle,
  workspaceTimezone?: string,
): CycleOccurrence[] => {
  const groups = new Map<string, ManagedSubmission[]>();
  submissions.forEach((submission) => {
    const key = getOccurrenceKey(submission, cycle, workspaceTimezone);
    groups.set(key, [...(groups.get(key) || []), submission]);
  });

  return Array.from(groups.entries())
    .sort(([left], [right]) => right.localeCompare(left))
    .map(([key, rows]) => ({
      key,
      label: dayjs(key).isValid()
        ? `Sent ${dayjs(key).format('MMMM D, YYYY')}`
        : key,
      submissions: rows,
    }));
};

export const attachCycleEnrichment = (
  submissions: ManagedSubmission[],
  enriched: ManagedSubmission[],
) => {
  const enrichedById = new Map(enriched.map((row) => [row.id, row]));
  return submissions.map((row) => {
    const match = row.id ? enrichedById.get(row.id) : undefined;
    return match && typeof match.cycle === 'object'
      ? {...row, cycle: match.cycle, cycleName: match.cycle.cycleName}
      : row;
  });
};

export const buildPseudoCycles = (
  submissions: ManagedSubmission[],
): ManagedCycle[] => {
  const cycles = new Map<string, ManagedCycle>();
  submissions.forEach((submission) => {
    const cycleId = getSubmissionCycleId(submission);
    if (!cycleId || cycles.has(cycleId)) return;
    if (typeof submission.cycle === 'object') {
      cycles.set(cycleId, submission.cycle);
      return;
    }
    cycles.set(cycleId, {
      id: cycleId,
      cycleName: submission.cycleName || 'Unnamed cycle',
      status: submission.isActive ? 'active' : 'ended',
      createdBy: '',
      createdAt: submission.createdAt,
      updatedAt: submission.updatedAt || submission.createdAt,
    });
  });
  return Array.from(cycles.values());
};

const weekdayIndex = (day?: string) =>
  [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ].indexOf((day || '').toLowerCase());

export const getNextRunDate = (
  cycle: ManagedCycle,
  now = new Date(),
  completedOccurrences = 0,
) => {
  if (getEffectiveCycleStatus(cycle) !== 'active' || !cycle.config) return null;
  if (
    cycle.config.endsAfterOccurrences &&
    completedOccurrences >= cycle.config.endsAfterOccurrences
  ) {
    return null;
  }

  const frequency = cycle.config.frequency?.toLowerCase();
  const start = dayjs(cycle.config.startDate || now).startOf('day');
  const today = dayjs(now).startOf('day');
  let candidate = today.isAfter(start) ? today : start;

  if (frequency === 'one-time') {
    candidate = start;
  } else if (frequency === 'daily') {
    const allowed = cycle.config.dayOfWeek
      ?.map(weekdayIndex)
      .filter((day) => day >= 0);
    if (allowed?.length) {
      while (!allowed.includes(candidate.day()))
        candidate = candidate.add(1, 'day');
    }
  } else if (frequency === 'weekly' || frequency === 'bi-weekly') {
    const day = weekdayIndex(cycle.config.dayOfWeek?.[0]);
    if (day >= 0) {
      while (candidate.day() !== day) candidate = candidate.add(1, 'day');
    }
    if (frequency === 'bi-weekly') {
      while (candidate.diff(start, 'week') % 2 !== 0)
        candidate = candidate.add(7, 'day');
    }
  } else if (frequency === 'monthly') {
    const day = Math.max(1, cycle.config.dayOfMonth || start.date());
    candidate = candidate.date(Math.min(day, candidate.daysInMonth()));
    if (candidate.isBefore(today)) {
      candidate = candidate.add(1, 'month');
      candidate = candidate.date(Math.min(day, candidate.daysInMonth()));
    }
  }

  if (candidate.isBefore(today)) return null;
  if (
    cycle.config.endDate &&
    candidate.isAfter(dayjs(cycle.config.endDate), 'day')
  ) {
    return null;
  }
  return candidate.toDate();
};

export const getCycleDestination = (
  cycle: ManagedCycle,
  tab: 'responses' | 'settings' = 'responses',
) => {
  if (getEffectiveCycleStatus(cycle) === 'draft') {
    return `/create-checkin-cycle?cycleId=${encodeURIComponent(
      cycle.id,
    )}&preview=true`;
  }

  return `/checkins/team-management/cycles/${cycle.id}?tab=${tab}`;
};

export const getParticipationDotColor = (
  percentage: number,
  submittedCount: number,
): string => {
  if (submittedCount === 0) return '#bfbfd4';
  if (percentage >= 70) return '#47b881';
  return '#e6821d';
};
